/**
 * Upload de anexos na ReplyBox (direct / indireto) com progresso.
 * DirectUpload: mostra chip + % enquanto o arquivo pesado sobe ao storage.
 * Indireto: anexa na hora; o progresso do POST fica no envio da mensagem.
 */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { checkFileSizeLimit } from 'shared/helpers/FileHelper';
import { getMaxUploadSizeByChannel } from '@chatwoot/utils';
import { DirectUpload } from 'activestorage';
import {
  DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { uploadProgressPercent } from 'dashboard/helper/uploadProgress';

export default {
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    installationLimit() {
      return resolveMaximumFileUploadSize(
        this.globalConfig.maximumFileUploadSize
      );
    },
  },

  methods: {
    maxSizeFor(mime) {
      if (this.isOnPrivateNote) {
        return this.installationLimit;
      }

      const channelType = this.inbox?.channel_type;

      if (!channelType || channelType === INBOX_TYPES.WEB) {
        return this.installationLimit;
      }

      const channelLimit = getMaxUploadSizeByChannel({
        channelType,
        medium: this.inbox?.medium,
        mime,
      });

      if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE) {
        return this.installationLimit;
      }

      return Math.min(channelLimit, this.installationLimit);
    },
    alertOverLimit(maxSizeMB) {
      useAlert(
        this.$t('CONVERSATION.FILE_SIZE_LIMIT', {
          MAXIMUM_SUPPORTED_FILE_UPLOAD_SIZE: maxSizeMB,
        })
      );
    },
    onFileUpload(file, conversationId = this.currentChat.id) {
      if (this.globalConfig.directUploadsEnabled) {
        this.onDirectFileUpload(file, conversationId);
      } else {
        this.onIndirectFileUpload(file, conversationId);
      }
    },

    setAttachmentUploadProgress(pendingId, percent) {
      const idx = this.attachedFiles.findIndex(f => f.pendingId === pendingId);
      if (idx === -1) return;
      this.attachedFiles.splice(idx, 1, {
        ...this.attachedFiles[idx],
        isUploading: percent < 100,
        uploadProgress: percent,
      });
    },

    onDirectFileUpload(file, conversationId = this.currentChat.id) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      // Chip imediato com progresso — arquivo pesado não some da UI sem feedback.
      const pendingId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      this.attachedFiles.push({
        pendingId,
        currentChatId: conversationId,
        resource: file,
        isPrivate: this.isPrivate,
        thumb: '',
        blobSignedId: undefined,
        isRecordedAudio: file?.isRecordedAudio || false,
        isUploading: true,
        uploadProgress: 0,
      });

      const upload = new DirectUpload(
        file.file,
        `/api/v1/accounts/${this.accountId}/conversations/${conversationId}/direct_uploads`,
        {
          directUploadWillCreateBlobWithXHR: xhr => {
            xhr.setRequestHeader(
              'api_access_token',
              this.currentUser.access_token
            );
          },
          directUploadWillStoreFileWithXHR: xhr => {
            xhr.upload.addEventListener('progress', event => {
              if (!event.lengthComputable) return;
              this.setAttachmentUploadProgress(
                pendingId,
                uploadProgressPercent(event.loaded, event.total)
              );
            });
          },
        }
      );

      upload.create((error, blob) => {
        if (error) {
          this.attachedFiles = this.attachedFiles.filter(
            f => f.pendingId !== pendingId
          );
          useAlert(error);
          return;
        }
        this.setAttachmentUploadProgress(pendingId, 100);
        this.attachFile({ file, blob, conversationId, pendingId });
      });
    },

    onIndirectFileUpload(file, conversationId = this.currentChat.id) {
      if (!file) return;

      const mime = file.file?.type || file.type;
      const maxSizeMB = this.maxSizeFor(mime);

      if (!checkFileSizeLimit(file, maxSizeMB)) {
        this.alertOverLimit(maxSizeMB);
        return;
      }

      this.attachFile({ file, conversationId });
    },
  },
};
